import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, Mail, Phone, User } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const token = process.env.REACT_APP_ADMIN_TOKEN || "";
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/contact`, { headers });
      setContacts(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to load submissions.";
      toast.error(msg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  return (
    <div className="min-h-screen bg-[#080808] text-white p-6 md:p-12">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="mb-4 -ml-4 text-zinc-400 hover:text-white hover:bg-white/5 rounded-none"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Landing
            </Button>
            <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tighter">
              Contact <span className="gold-gradient-text">Submissions</span>
            </h1>
            <p className="text-zinc-400 mt-2">
              Viewing all inbound leads from the landing page.
            </p>
          </div>

          <Button
            onClick={fetchContacts}
            disabled={loading}
            className="rounded-none bg-[#D4AF37] hover:bg-[#F3C945] text-black font-medium"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh Data
          </Button>
        </div>

        <div className="border border-white/10 bg-[#0C0C0C] overflow-hidden">
          <Table>
            <TableHeader className="bg-white/5">
              <TableRow className="border-b border-white/10 hover:bg-transparent">
                <TableHead className="text-zinc-400 font-medium py-4">Date</TableHead>
                <TableHead className="text-zinc-400 font-medium py-4">Contact</TableHead>
                <TableHead className="text-zinc-400 font-medium py-4">Role</TableHead>
                <TableHead className="text-zinc-400 font-medium py-4">Message</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-48 text-center text-zinc-500">
                    Loading submissions...
                  </TableCell>
                </TableRow>
              ) : contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-48 text-center text-zinc-500">
                    No submissions found yet.
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((c) => (
                  <TableRow
                    key={c.id}
                    className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                  >
                    <TableCell className="align-top py-6 font-mono text-[11px] text-zinc-500">
                      {new Date(c.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="align-top py-6 min-w-[200px]">
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-2 text-white font-medium">
                          <User className="w-3.5 h-3.5 text-[#D4AF37]" />
                          {c.name}
                        </div>
                        <div className="flex items-center gap-2 text-zinc-400 text-sm">
                          <Mail className="w-3.5 h-3.5" />
                          {c.email}
                        </div>
                        {c.phone && (
                          <div className="flex items-center gap-2 text-zinc-400 text-sm">
                            <Phone className="w-3.5 h-3.5" />
                            {c.phone}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="align-top py-6">
                      {c.role ? (
                        <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20 rounded-none font-normal">
                          {c.role}
                        </Badge>
                      ) : (
                        <span className="text-zinc-600 text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell className="align-top py-6 max-w-md">
                      <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap">
                        {c.message}
                      </p>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default Admin;
